// Uploading a document to an automation idea (owner, 2026-08-19: *"i need the
// ideas to store attachment since its being sent to me"*).
//
// The bytes go up as the request BODY, with the name in a header — the Worker
// stores them in the ALL Managers shared drive and records a link to our copy.
// Not multipart: a Worker parsing multipart to find one file is a parser
// nobody needs.
//
// This is the one write in the client that does not go through `authedWrite`,
// because that helper JSON-encodes its body. Everything else it does — the
// bearer token, the Idempotency-Key, the view-as lens — is done here by hand
// and deliberately in the same order.
import { API_BASE } from './config';
import { authToken } from './auth';
import { withViewAs } from './viewAs';

/** Same ceiling the server enforces, stated here so the refusal happens before
 * a 25 MB upload rather than after it. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export interface UploadedAttachment {
  id: number;
  url: string;
  name: string | null;
}

export async function uploadIdeaFile(ideaId: number, file: File): Promise<UploadedAttachment> {
  if (file.size === 0) throw new Error(`${file.name} is empty`);
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1048576)} MB — the limit is `
      + `${MAX_UPLOAD_BYTES / 1048576} MB. Put it in Drive and paste the link instead.`,
    );
  }

  const token = authToken.value;
  const res = await fetch(`${API_BASE}${withViewAs(`/api/v1/automation-ideas/${ideaId}/files`)}`, {
    method: 'POST',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'content-type': file.type || 'application/octet-stream',
      // encodeURIComponent because a header may not carry a non-ASCII
      // character, and a manager's phone photo can be named anything at all.
      'x-file-name': encodeURIComponent(file.name),
      // One key per ACTION, so a double-click replays instead of storing the
      // file twice — the same rule authedWrite follows.
      'idempotency-key': crypto.randomUUID(),
    },
    body: file,
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error || `upload failed (HTTP ${res.status})`);
  }
  return (body as { attachment: UploadedAttachment }).attachment;
}
